export const GLOBAL_BASE_CURRENCY = "AUD";

export const SETTINGS_PATH = "/settings";
export const SETTINGS_SECURITY_PATH = `${SETTINGS_PATH}/security`;

export const OTHER = "Other" as const;

export const isOther = (value?: string): boolean =>
  value?.trim().toLowerCase() === OTHER.toLowerCase();

export const GLOSS_VAULT = "GLOSS Vault" as const;
export const GLOSS_CREDITS_ACCOUNT_NAME = "GLOSS Credits" as const;
export const GLOSS_CREDITS_SYMBOL = "CrS" as const;
export const GLOSS_PLATINUM_CREDITS_SYMBOL = "CrP" as const;

// True for any GLOSS credit symbol (CrS/CrP); add a new credit tier here, not at call sites.
export const isGlossCreditsSymbol = (code?: string): boolean =>
  code === GLOSS_CREDITS_SYMBOL || code === GLOSS_PLATINUM_CREDITS_SYMBOL;

export const TAGS = {
  ALL: "all",
  ACCOUNT: "account",
  TRANSACTION: "transaction",
  CATEGORY: "category",
  CABINET: "cabinet",
  SYMBOL: "symbol",
  PLUGIN: "plugin",
} as const;
export type Tag = (typeof TAGS)[keyof typeof TAGS];

export const DESKTOP_DIALOG = "no-background-dialog"; // todo align this class naming
export const MOBILE_DIALOG = "mobile-dialog";
export const MOBILE_DIALOG_MIDDLE = "mobile-dialog-middle";
export const RIGHT_PANEL_DIALOG = "right-panel-dialog";
// Added by mobileSlideInConfig() (components/dialog/mobile-slide-in-dialog.ts), not set directly.
export const MOBILE_SLIDE_IN_DIALOG = "mobile-slide-in-dialog";
// disableClose is intentionally omitted — callers set it as needed (default: false).
// Slide-outs that carry input or a request should open through RightPanelService instead: it
// closes on click-away/Escape but not while busy, and asks before discarding edited input.
// Animation timing/easing lives in material-ui.scss (.right-panel-dialog) per the
// GLOSS DS 2.1 Motion · Slide-out spec — exitAnimationDuration must match its exit keyframe duration.
export const RIGHT_PANEL_CONFIG = {
  panelClass: RIGHT_PANEL_DIALOG,
  position: { right: "0", top: "0" },
  height: "100vh",
  enterAnimationDuration: "0ms",
  // Angular Material starts this 200ms teardown timer immediately but adds the CSS class that
  // actually triggers the slide-out keyframe one requestAnimationFrame later
  // (@angular/material dialog.mjs, _startExitAnimation), so the container is disposed at
  // roughly 92% travel on desktop - clipped by ~1 frame (#4406). Not worth chasing on its own:
  // it's ~16ms of a 200ms slide, a large improvement on the exit never firing at all pre-#4335,
  // and "fixing" it means bumping both this and --duration-normal to ~220ms - that token is
  // shared with the fade-out and mobile slide-out keyframes in material-ui.scss, so it needs a
  // real-browser check this environment can't do. If it ever looks wrong in practice, that's
  // the fix.
  exitAnimationDuration: "200ms",
  // Keeps the default dark backdrop; "right-panel-backdrop" only overrides its fade timing
  // (see material-ui.scss) to match the panel's motion spec — darkness/color is untouched.
  // Explicitly typed as string[] (not readonly) — MatDialogConfig.backdropClass rejects the
  // readonly tuple that `as const` below would otherwise infer. Object.freeze() is a separate,
  // runtime-only guard (#4406): every `{ ...RIGHT_PANEL_CONFIG }` spread copies this array's
  // reference, not its contents, so an accidental future mutation at one call site would
  // silently corrupt every other spread of this config. Nothing mutates it today; this just
  // turns a latent footgun into an immediate TypeError if that ever changes.
  backdropClass: Object.freeze(["cdk-overlay-dark-backdrop", "right-panel-backdrop"]) as string[],
} as const;

export const ALIGNMENT_BALANCE_RESET: string = "Align. Balance Reset";
export const REVALUATION: string = "Revaluation";

export const IRONFLY_TECHNOLOGIES: string = "Powered by Ironfly Technologies";

// todo replace for the new Allowed Country list
export const COUNTRIES: { code: string; name: string }[] = [
  { code: "AU", name: "Australia" },
  { code: "HK", name: "Hong Kong" },
  { code: "US", name: "United States" },
];
